// src/styled/common.js
import styled, { css, keyframes  } from 'styled-components';
import { boxShadow, borderRadius } from '../styles/mixins';
import { FaSyncAlt } from "react-icons/fa"; 
import { FaSpinner } from 'react-icons/fa'; 

// Container Styles
export const Container = styled.div`
  width: 100%;
  max-width: 1500px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.padding};
  background-color: ${({ theme }) => theme.backgroundColor};
  ${boxShadow};
  ${borderRadius};
  box-sizing: border-box;

  @media (min-width: 768px) {
    max-width: 95%;
  }

  @media (min-width: 1024px) {
    max-width: 95%;
  }
`;

export const ListContainer = styled.div`
  border: 1px solid #e0e0e0;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  background-color: #fff;
  margin-top: 20px;
`;

export const FlexContainer = styled.div`
  display: flex;
  flex-wrap: wrap; /* 🔥 New line to prevent overflow */
  ${({ direction }) => direction && `flex-direction: ${direction};`}
  ${({ align }) => align && `align-items: ${align};`}
  ${({ justify }) => justify && `justify-content: ${justify};`}
  ${({ gap }) => gap && `gap: ${gap};`}
  width: 100%;
  box-sizing: border-box;
`;

export const Heading = styled.h2`
  text-align: center;
  color: ${({ theme }) => theme.primaryColor};
  font-size: ${({ theme }) => theme.headingFontSize};
  font-weight: 600; /* Semi-bold for a modern look */
  letter-spacing: 0.8px; /* Slightly increased for elegance */
  line-height: 1.5; /* Balanced line height for readability */
  margin: ${({ theme }) => theme.margin} 0; /* Use theme margin */
  text-transform: capitalize; /* Clean and professional */
  font-family: ${({ theme }) => theme.fontFamily}, 'Inter', sans-serif; /* Fallback to 'Inter' */
  background: ${({ theme }) =>
    `linear-gradient(90deg, ${theme.primaryColor}, ${theme.secondaryColor})`};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent; /* Trendy gradient text */
  padding: 8px 16px; /* Slight padding for emphasis */
  border-radius: ${({ theme }) => theme.borderRadius}; /* Rounded edges for a polished look */
  @media (max-width: 768px) {
  font-size: 1.4rem;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }  
`;


export const ButtonBase = styled.button`
  padding: ${({ theme }) => theme.padding};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius};
  cursor: pointer;
  transition: background-color 0.3s ease;
  font-family: ${({ theme }) => theme.fontFamily}; /* Use the font-family from theme */

  ${({ variant, theme }) =>
    variant === 'primary' &&
    css`
      background-color: ${theme.primaryColor};
      color: white;

      &:hover {
        background-color: ${theme.primaryHoverColor};
      }
    `}

  ${({ variant, theme }) =>
    variant === 'secondary' &&
    css`
      background-color: ${theme.secondaryColor};
      color: white;

      &:hover {
        background-color: ${theme.secondaryHoverColor};
      }
    `}
`;

export const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: ${({ theme }) => theme.borderRadius};
  font-size: ${({ theme }) => theme.textFontSize};
`;

export const Select = styled.select`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: ${({ theme }) => theme.borderRadius};
  font-size: ${({ theme }) => theme.textFontSize};
`;

export const Error = styled.p`
  color: red;
  text-align: center;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: ${({ theme }) => theme.margin};
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  ${boxShadow}
  ${borderRadius}
  overflow: hidden;
  table-layout: auto;
  
  
  thead {
    background-color: ${({ theme }) => theme.secondaryBackground};
  }

  th, td {
    padding: 8px;
    text-align: left;
    border: 1px solid #ddd;
  }

  /* 📱 Mobile view */
  @media (max-width: 768px) {
    display: block;

    thead {
      display: none; /* Hide top header */
    }

    tbody {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    tr {
      display: flex;
      flex-direction: column;
      background: ${({ theme }) => theme.backgroundColor};
      border: 1px solid #ccc;
      border-radius: 12px;
      padding: 12px;
    }

    td {
      display: flex;
      flex-direction: column;
      padding: 6px 0;
      border: none;
      border-bottom: 1px solid #eee;
    }

    td:last-child {
      border-bottom: none;
    }

    td::before {
      content: attr(data-label);
      font-weight: bold;
      font-size: 0.9rem;
      margin-bottom: 4px;
      color: #444;
    }
  }
`;

export const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f9f9f9;
  }

  &:hover {
    background-color: #f1f1f1;
    cursor: pointer;
  }
`;

export const TableData = styled.td`
  padding: 0.5rem;
  border-bottom: 1px solid #ddd;
  word-break: break-word;
  max-width: 100%;
`;

export const StatusBadge = styled.span`
  display: inline-block;
  width: 18px;
  height: 18px;
  border-radius: 100%;
  margin-right: 10px;
  ${(props) => {
    const status = props.status?.toLowerCase();
    return (
      status === 'red' &&
      css`
        background-color: #e74c3c;
      `
    );
  }}
  ${(props) => {
    const status = props.status?.toLowerCase();
    return (
      status === 'yellow' &&
      css`
        background-color: #f1c40f;
      `
    );
  }}
  ${(props) => {
    const status = props.status?.toLowerCase();
    return (
      status === 'green' &&
      css`
        background-color: #2ecc71;
      `
    );
  }}
`;

export const StatusText = styled.span`
  vertical-align: middle;
  text-transform: capitalize;
`;

const spin = keyframes`
  0% { 
    transform: rotate(0deg); 
  }
  100% { 
    transform: rotate(360deg); 
  }
`;

export const SpinningIcon = styled(FaSpinner)`
  font-size: 16px;
  animation: ${spin} 1s linear infinite;
`;

export const LoadingSpinner = styled.div`
  border: 4px solid ${({ theme }) => theme.backgroundColor}; /* Light grey */
  border-top: 4px solid ${({ theme }) => theme.primaryColor}; /* Blue */
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: ${spin} 1s linear infinite; /* Use the spin animation */
`;

export const LoadingContainer = styled(FlexContainer)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

export const DetailContainer = styled.div`
  max-width: 1000px;
  margin: 20px auto;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #fff;
  position: relative;
`;

export const CloseButton = styled(ButtonBase)`
  position: sticky;
  top: 0;
  right: 0;
  margin-left: auto;
  z-index: 10;
  padding: 5px 10px;
  font-size: 14px;
  border-radius: 4px;
  background-color: #ff5c5c;
  color: white;

  &:hover {
    background-color: #e04b4b;
  }
`;


export const DetailTable = styled(Table)`
  margin-top: 20px;
  width: 100% !important;     // 👈 force full width
  table-layout: auto;         // 👈 allow cells to grow
`;

export const DetailHeader = styled.th`
  padding: 10px;
  background-color: #f4f4f4;
  border-bottom: 2px solid #ddd;
  text-align: left;
`;

export const DetailRow = styled(TableRow)`
  &:hover {
    background-color: ${({ theme }) => theme.backgroundColor};
  }
`;

export const DetailData = styled(TableData)`
  word-break: break-word;

  ${({ isClickable, theme }) =>
    isClickable &&
    `
      cursor: pointer;
      color: ${theme.primaryColor};

      &:hover {
        color: ${theme.primaryHoverColor};
      }
    `}
`;

// Modal Components
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

export const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  max-width: 1200px; /* ⬅️ wider for desktop */
  width: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  max-height: 90vh;
  z-index: 1000;
  position: relative;

  @media (max-width: 768px) {
    max-width: 90%;
    padding: 16px 12px;
    margin: 0 auto;
    max-height: 90vh;                        /* 💡 Prevent overflow */
    overflow-y: auto;                        /* 💡 Scrollable content */
    -webkit-overflow-scrolling: touch;      /* ✅ Smooth scroll on mobile */
  }

  @media (max-width: 480px) {
    max-width: calc(100vw - 32px);           /* 💡 Leaves 16px margin on both sides */
  }
`;

// Hero and Features Section
export const HeroSection = styled.section`
  display: flex;
  flex-direction: column;
  padding: 40px 5vw;
  background-color: #ffffff;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 40px;
  }
`;

export const HeroContent = styled.div`
  max-width: 600px;
  padding-right: 20px;

  h1 {
    font-size: 48px;
    font-weight: 700;
    margin-bottom: 20px;
  }

  p {
    font-size: 18px;
    margin-bottom: 30px;
    line-height: 1.6;
    color: #555;
  }

  button {
    font-size: 18px;
    padding: 12px 24px;
  }
`;

export const HeroImage = styled.div`
  flex: 1;
  img {
    width: 100%;
    height: auto;
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

export const FeatureCard = styled.div`
  text-align: center;
  padding: 20px;
  background-color: #f2f2f2;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 28%;
  height: 350px; /* Set a fixed height */
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }

  img {
    width: 50px;
    margin-bottom: 20px;
  }

  h3 {
    margin-bottom: 10px;
    font-size: 22px;
    font-weight: 600;
    color: ${({ theme }) => theme.primaryColor};
  }

  p {
    color: ${({ theme }) => theme.textColor};
  }
`;

export const FormContainer = styled.div`
  width: 100%;
  padding: 20px;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: ${({ theme }) => theme.borderRadius};
  box-sizing: border-box;
  overflow-x: auto;
`;

export const SalesItemsContainer = styled.div`
  width: 100%;
  padding: 20px;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: ${({ theme }) => theme.borderRadius};
  box-sizing: border-box;
  margin-top: 20px;
  overflow-x: auto;
`;

export const FieldContainer = styled.div`
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
`;

export const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
`;

export const StyledTextarea = styled.textarea`
  height: 80px;
  padding: 10px;
  font-size: 14px;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 5px;
  resize: vertical;
  margin-bottom: 15px;

  &:focus {
    outline: none;
    border-color: #007bff;
    background-color: #fff;
  }

  &::placeholder {
    color: #999;
  }
`;

export const SideBySideContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

export const StockCounter = styled.div`
  margin: 10px;
  padding: 1px;
  font-size: 15px;
  font-weight: 800;
  font-family: sans-serif;
  color: ${(props) => (props.stock > 0 ? "#82dd55" : "#e23636")};
  background-color: ${(props) => (props.stock > 0 ? "#f0ffe6" : "#ffe6e6")};
  border: 1px solid ${(props) => (props.stock > 0 ? "#82dd55" : "#e23636")};
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease-in-out;
`;

export const PlusButton = styled.button`
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  padding: 0; /* ❗ Ensure no padding skews the shape */
  line-height: 1; /* ❗ Prevent vertical stretching */

  transition: background-color 0.3s ease;

  &:hover {
    background-color: #218838;
  }

  &:active {
    background-color: #1e7e34;
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 480px) {
    width: 24px;
    height: 24px;
    font-size: 14px;
    margin-left: 4px;
  }
`;

export const ExpandButton = styled(ButtonBase)`
  background-color: #e74c3c;
  color: white;
  padding: 10px 20px;
  margin-bottom: 10px;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: #c0392b;
  }
`;

const StyledRefreshButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: #e0e0e0;
  }

  &:focus {
    outline: none;
  }

  svg {
    font-size: 18px;
    color: #333;
  }
`;

export const RefreshButton = ({ onClick, title = 'Refresh', ...props }) => (
  <StyledRefreshButton onClick={onClick} title={title} {...props}>
    <FaSyncAlt />
  </StyledRefreshButton>
);

export const QuantityContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    justify-content: space-between;
    width: 100%;
  }
`;

export const QuantityText = styled.span`
  font-weight: bold;
  min-width: 24px;
`;
